import {
  KeyTranslation,
  ProjectKeysStatuses,
  ProjectKeyStatuses,
  ProjectRevision,
  ScreenshotKeyConnection,
  ScreenshotKeyConnectionStyles,
} from '@bumpy/i18n_common';

export function areKeyTranslationsSame(
  langs: string[],
  a: KeyTranslation,
  b: KeyTranslation,
): boolean {
  for (const lang of langs) {
    if (a[lang] !== b[lang]) {
      return false;
    }
  }
  return true;
}

export function areKeyStatusesApproved(
  langs: string[],
  keyStatuses: ProjectKeyStatuses,
): boolean {
  const statusLangs = Object.keys(keyStatuses);
  if (statusLangs.length !== langs.length) {
    return false;
  }
  for (const lang of langs) {
    const keyStatus = keyStatuses[lang];
    if (!keyStatus) {
      return false;
    }
    const status = keyStatus.status;
    if (status !== 'Approved') {
      return false;
    }
  }
  return true;
}

export function areKeyStatusesSame(
  langs: string[],
  a: ProjectKeyStatuses,
  b: ProjectKeyStatuses,
): boolean {
  for (const lang of langs) {
    const aStatus = a[lang];
    const bStatus = b[lang];

    if (aStatus === undefined && bStatus === undefined) {
      continue;
    }

    if (aStatus === undefined && bStatus !== undefined) {
      return false;
    }

    if (bStatus === undefined && aStatus !== undefined) {
      return false;
    }

    if (aStatus.status !== bStatus.status || aStatus.text !== bStatus.text) {
      return false;
    }
  }
  return true;
}

export function areRevisionsSame(
  a: ProjectRevision,
  b: ProjectRevision,
): boolean {
  if (a.keys.length !== b.keys.length) {
    return false;
  }
  for (let i = 0; i < a.keys.length; ++i) {
    if (a.keys[i] !== b.keys[i]) {
      return false;
    }
  }
  if (
    JSON.stringify(a.translationIndex) !== JSON.stringify(b.translationIndex)
  ) {
    return false;
  }

  const aLangs = Object.keys(a.translations);
  const bLangs = Object.keys(b.translations);
  if (aLangs.length !== bLangs.length) {
    return false;
  }
  for (let i = 0; i < aLangs.length; ++i) {
    if (aLangs[i] !== bLangs[i]) {
      return false;
    }
  }
  for (const lang of aLangs) {
    const aKeys = Object.keys(a.translations[lang]);
    const bKeys = Object.keys(b.translations[lang]);
    if (aKeys.length !== bKeys.length) {
      return false;
    }
    if (JSON.stringify(aKeys) !== JSON.stringify(bKeys)) {
      return false;
    }
    for (const key of aKeys) {
      if (a.translations[lang][key] !== b.translations[lang][key]) {
        return false;
      }
    }
  }
  return true;
}

export function areKeysStatusesSame(
  a: ProjectKeysStatuses,
  b: ProjectKeysStatuses,
): boolean {
  const aLangs = Object.keys(a);
  const bLangs = Object.keys(b);
  if (aLangs.length !== bLangs.length) {
    return false;
  }
  if (JSON.stringify(aLangs) !== JSON.stringify(bLangs)) {
    return false;
  }
  for (const lang of aLangs) {
    const aKeys = Object.keys(a[lang]);
    const bKeys = Object.keys(b[lang]);
    if (aKeys.length !== bKeys.length) {
      return false;
    }
    if (JSON.stringify(aKeys) !== JSON.stringify(bKeys)) {
      return false;
    }

    for (const key of aKeys) {
      const aStatus = a[lang][key];
      const bStatus = b[lang][key];

      if (aStatus.status !== bStatus.status) {
        return false;
      }
      if (aStatus.text !== bStatus.text) {
        return false;
      }
    }
  }
  return true;
}

export function areSameConnections(
  a: ScreenshotKeyConnection[],
  b: ScreenshotKeyConnection[],
): boolean {
  if (a.length !== b.length) {
    return false;
  }
  for (let i = 0; i < a.length; i++) {
    const connA = a[i];
    const connB = b[i];
    if (
      connA.key !== connB.key ||
      connA.x !== connB.x ||
      connA.y !== connB.y ||
      connA.width !== connB.width ||
      connA.height !== connB.height ||
      !areStylesSame(connA.styles, connB.styles)
    ) {
      return false;
    }
  }
  return true;
}

function areStylesSame(
  a?: ScreenshotKeyConnectionStyles,
  b?: ScreenshotKeyConnectionStyles,
): boolean {
  if (a === undefined && b === undefined) {
    return true;
  }
  if (a === undefined && b !== undefined) {
    return false;
  }
  if (a !== undefined && b === undefined) {
    return false;
  }

  return (
    a!.background === b!.background &&
    a!.fontStyle === b!.fontStyle &&
    a!.fontSize === b!.fontSize &&
    a!.color === b!.color
  );
}
